import { useLocation } from "wouter";
import type { Claim, ClaimStatusTemplate } from "@/types/claims";
import type { ClaimFolder } from "@/api/generated/client/models/ClaimFolder";
import type { ClaimPipeline } from "@/api/generated/client/models/ClaimPipeline";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Archive, Trash2, Undo2 } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { FolderPicker } from "@/components/claims/folders/FolderPicker";
import { ClaimAssigneePicker } from "@/components/claims/ClaimAssigneePicker";
import { formatDate } from "@/lib/utils";
import { ClaimRowStatusSelect } from "@/components/claims/ClaimRowStatusSelect";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

interface ClaimsTableProps {
    claims: Claim[];
    statuses: Pick<ClaimStatusTemplate, "id" | "pipelineId" | "displayName" | "color">[];
    pipelines?: ClaimPipeline[];
    assignablePipelines?: ClaimPipeline[];
    viewerCompanyId?: number | null;
    /** Full accessible folder list — used to render a claim's current folder label. */
    folders: ClaimFolder[];
    /** Subset the user can assign into; defaults to `folders`. */
    assignableFolders?: ClaimFolder[];
    onStatusChange: (id: number, statusTemplateId: number) => void;
    onPipelineChange?: (id: number, pipelineId: number) => void;
    onFolderChange: (id: number, folderId: number | null) => void;
    onArchiveToggle: (id: number, isArchived: boolean) => void;
    onDeleteClick: (id: number) => void;
    isArchiving?: boolean;
    isLoading: boolean;
}

export function ClaimsTable({
    claims,
    statuses,
    pipelines = [],
    assignablePipelines = [],
    viewerCompanyId = null,
    folders,
    assignableFolders,
    onStatusChange,
    onPipelineChange = () => undefined,
    onFolderChange,
    onArchiveToggle,
    onDeleteClick,
    isArchiving = false,
    isLoading,
}: ClaimsTableProps) {
    const [, setLocation] = useLocation();

    if (isLoading) {
        return <div className="space-y-4 p-4">
            {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />
            ))}
        </div>;
    }

    if (claims.length === 0) {
        return (
            <div className="text-center py-12 text-muted-foreground border rounded-md">
                No claims found matching your criteria.
            </div>
        );
    }

    return (
        <TooltipProvider delayDuration={300}>
            <div className="rounded-md border bg-card text-card-foreground overflow-x-auto md:overflow-visible">
                <Table className="min-w-[1160px]">
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[140px]">Claim #</TableHead>
                        <TableHead className="min-w-[150px]">Insured Name</TableHead>
                        <TableHead className="min-w-[220px]">Property Address</TableHead>
                        <TableHead className="w-[120px]">DOL</TableHead>
                        <TableHead className="w-[120px]">Created</TableHead>
                        <TableHead className="w-[170px]">Assigned To</TableHead>
                        <TableHead className="w-[160px]">Folder</TableHead>
                        <TableHead className="min-w-[140px]">Workflow</TableHead>
                        <TableHead className="w-[150px]">Status</TableHead>
                        <TableHead className="w-[96px] text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {claims.map((claim) => {
                        const archived = claim.isArchived;
                        const pipelineOptions = claim.companyId === viewerCompanyId ? assignablePipelines : [];
                        const canChangePipeline = (claim.canWrite ?? false) && pipelineOptions.length > 0;
                        const currentPipeline = pipelines.find((pipeline) => pipeline.id === claim.pipelineId);
                        const pipelineName = currentPipeline?.name ?? claim.claimPipelineName ?? null;
                        const pipelineColor = currentPipeline?.color ?? claim.claimPipelineColor ?? "#94A3B8";
                        return (
                        <TableRow
                            key={claim.id}
                            className="group cursor-pointer hover:bg-muted/50"
                            onClick={() => setLocation(`/claims/${claim.id}`)}
                        >
                            <TableCell className="font-mono text-xs text-muted-foreground whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                    <span>{claim.claimNumber}</span>
                                    {!(claim.canWrite ?? false) && (
                                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 text-amber-700 border-amber-300 bg-amber-50">
                                            View only
                                        </Badge>
                                    )}
                                    {archived && (
                                        <Badge variant="secondary" className="text-[10px] h-5 px-1.5">
                                            Archived
                                        </Badge>
                                    )}
                                </div>
                            </TableCell>
                            <TableCell className="font-medium max-w-[160px] truncate" title={claim.insuredName || ""}>
                                {claim.insuredName}
                            </TableCell>
                            <TableCell className="max-w-[240px] truncate" title={claim.propertyAddress || ""}>
                                {claim.propertyAddress || "—"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                                {formatDate(claim.dateOfLoss)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-muted-foreground">
                                {formatDate(claim.createdAt)}
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                                <ClaimAssigneePicker claim={claim} />
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                                {folders.length > 0 ? (
                                    <FolderPicker
                                        folders={folders}
                                        assignableFolders={assignableFolders ?? folders}
                                        value={claim.claimFolderId}
                                        disabled={!(claim.canWrite ?? false)}
                                        onChange={(folderId) => onFolderChange(claim.id, folderId)}
                                    />
                                ) : (
                                    <span className="text-xs text-muted-foreground">—</span>
                                )}
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                                {claim.pipelineId ? (
                                    canChangePipeline ? (
                                    <Select
                                        value={claim.pipelineId?.toString()}
                                        onValueChange={(pipelineId) => onPipelineChange(claim.id, parseInt(pipelineId))}
                                    >
                                        <SelectTrigger className="h-8 w-[130px] text-xs">
                                            <SelectValue>
                                                {pipelineName ? (
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <span
                                                            className="h-2.5 w-2.5 rounded-full border"
                                                            style={{ backgroundColor: pipelineColor }}
                                                        />
                                                        <span className="truncate">{pipelineName}</span>
                                                    </span>
                                                ) : (
                                                    <span className="text-muted-foreground">No Workflow</span>
                                                )}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {pipelineOptions.map((pipeline) => (
                                                <SelectItem key={pipeline.id} value={pipeline.id.toString()}>
                                                    <span className="inline-flex items-center gap-2">
                                                        <span
                                                            className="h-2.5 w-2.5 rounded-full border"
                                                            style={{ backgroundColor: pipeline.color ?? "#94A3B8" }}
                                                        />
                                                        {pipeline.name}
                                                    </span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    ) : (
                                        <Badge variant="outline" className="h-8 w-[130px] min-w-0 justify-start truncate rounded-md px-2 py-0 text-xs font-medium" style={{ borderColor: pipelineColor }} title={pipelineName ?? "No Workflow"}>
                                            {pipelineName ?? "No Workflow"}
                                        </Badge>
                                    )
                                ) : (
                                    <span className="text-xs text-muted-foreground">—</span>
                                )}
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                                <ClaimRowStatusSelect
                                    claim={claim}
                                    statuses={statuses}
                                    viewerCompanyId={viewerCompanyId}
                                    onChange={(statusTemplateId) => onStatusChange(claim.id, statusTemplateId)}
                                    className="w-[130px] h-8 text-xs"
                                />
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                                {claim.canWrite ?? false ? (
                                    <div className="flex items-center gap-1">
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                                                    onClick={() => onArchiveToggle(claim.id, !archived)}
                                                    disabled={isArchiving}
                                                    aria-label={archived ? `Unarchive claim ${claim.claimNumber}` : `Archive claim ${claim.claimNumber}`}
                                                >
                                                    {archived ? <Undo2 className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                {archived ? "Unarchive" : "Archive"}
                                            </TooltipContent>
                                        </Tooltip>

                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-destructive opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/10"
                                                    onClick={() => onDeleteClick(claim.id)}
                                                    aria-label={`Delete claim ${claim.claimNumber}`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>Delete</TooltipContent>
                                        </Tooltip>
                                    </div>
                                ) : null}
                            </TableCell>
                        </TableRow>
                        );
                    })}
                </TableBody>
                </Table>
            </div>
        </TooltipProvider>
    );
}
